import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createGrievanceStatus, getAllGrievanceStatuses, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const GrievanceStatusListComponent = ({ exception, onClose, onResolved }) => {
    const [grievanceStatusName, setGrievanceStatusName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [grievanceStatuses, setGrievanceStatuses] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractGrievanceStatusDetails = () => {
            setGrievanceStatusName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractGrievanceStatusDetails();
        }

        const fetchGrievanceStatuses = async () => {
            setLoading(true);
            try {
                const response = await getAllGrievanceStatuses();
                const statusesData = response?.data?.data || [];
                setGrievanceStatuses(Array.isArray(statusesData) ? statusesData : []);
            } catch (error) {
                console.error('Failed to fetch grievance statuses', error);
                message.error('Failed to fetch grievance statuses');
            } finally {
                setLoading(false);
            }
        };

        fetchGrievanceStatuses();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (grievanceStatusName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        id: 0,
                        name: grievanceStatusName,
                        alias_for: null,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };
                    
                    await createGrievanceStatus(payload);
                    await updateException(exception.id);
                    message.success('Grievance status created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    message.error('Failed to create grievance status');
                } finally {
                    setLoading(false);
                }
            } else {
                console.warn("Exception already resolved.");
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Grievance status name not found.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleGrievanceStatusSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    id: 0,
                    name: grievanceStatusName,
                    alias_for: selectedAliasId,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                };
                
                await createGrievanceStatus(payload);
                await updateException(exception.id);
                console.log(`Grievance status "${grievanceStatusName}" set as alias for status with ID: ${selectedAliasId}`);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                console.error(`Failed to update alias for grievance status with ID: ${selectedAliasId}`, error);
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            console.warn("Exception already resolved.");
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {grievanceStatusName ? `Select an existing grievance status to alias "${grievanceStatusName}" to.` : 'Select an existing grievance status to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a grievance status"
                                    onChange={handleGrievanceStatusSelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {grievanceStatuses.map((status) => (
                                        <Option key={status.id} value={status.id}>
                                            {status.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default GrievanceStatusListComponent;
